import React, { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAdminAccommodationsTelemetry } from './Accommodations.telemetry';
import { useNavigationPaneContext } from '@website-editor/context/NavigationPaneContext/NavigationPane.context';
import { ACCOMMODATIONS_ACTIONS } from './Accommodations.constants';
import { Add, AddCircle, Mobile } from '@withjoy/joykit/icons';
import { useWebsiteEditorContext } from '@website-editor/context/WebsiteEditorContext/WebsiteEditor.context';
import { useWebsiteEditorCustomPageTelemetry } from '@website-editor/features/custom-page/WebsiteEditorCustomPage.telemetry';
import { FabButtonProps } from '@withjoy/joykit/components/FabButton';
import { useAccommodationsContext } from '@website-editor/customComponents/Accommodations/context/useAccommodationsContext';
import { PageFabArea } from '@website-editor/components/PageFabArea';

export const AccommodationsFab: React.FC = () => {
  const { t } = useTranslation('websiteEditor');
  const panelTrans = t('panel');
  const hotelTrans = t('customPage', 'travelMap', 'hotel');
  const { pageSlug, pageConfig, isSuperAdmin } = useAccommodationsContext();

  const telemetry = useAdminAccommodationsTelemetry();
  const telemetryCustomPage = useWebsiteEditorCustomPageTelemetry();
  const { navigateToScreen } = useNavigationPaneContext();
  const { isVisiblePreview, togglePreviewVisibility } = useWebsiteEditorContext();

  const handleAddAccommodations = useEventCallback(() => {
    telemetry.addAccommodationClicked(pageSlug, { isSuperAdmin, category: 'admin.accommodations' });
    navigateToScreen(ACCOMMODATIONS_ACTIONS.ACCOMMODATIONS_ADD);
  });

  const addAction = useMemo(() => {
    return {
      label: hotelTrans.addPlaceToStay(),
      icon: <AddCircle />,
      mobileIcon: <Add />,
      onClick: handleAddAccommodations,
    };
  }, [handleAddAccommodations, hotelTrans]);

  const togglePreviewEdit = useEventCallback(() => {
    if (isVisiblePreview) {
      telemetryCustomPage.editClickedMobile(pageSlug);
    } else {
      telemetryCustomPage.previewClickedMobile(pageSlug);
    }
    togglePreviewVisibility();
  });

  const preview: FabButtonProps = { variant: 'ghostShadow', children: panelTrans.viewPage(), startIcon: <Mobile />, onClick: togglePreviewEdit };

  return (
    <PageFabArea
      zIndex={'docked'}
      tooltip={hotelTrans.fabTooltip()}
      preview={preview}
      addAction={addAction}
      hidePageSettingsAndFabCTA={pageConfig.hidePageSettingsAndFabCTA}
    />
  );
};
